/** @jsxImportSource @emotion/react */
import React, { useState } from 'react';
import { Button, Card, Empty, Form, InputNumber, Input, Modal, Space, Table, Tag, Typography, message } from 'antd';
import {
  useGetLiveAssetsQuery,
  useGetLiveAssetReservationsQuery,
  useReserveLiveAssetMutation,
  useReleaseLiveAssetReservationMutation,
} from '../../api/liveAssetsApi';
import type { LiveAsset } from '../../types/inventory';

const { Text } = Typography;

const LiveAssetsSection: React.FC<{ saveId: string }> = ({ saveId }) => {
  const { data: assets = [], isLoading } = useGetLiveAssetsQuery({ saveId });
  const { data: reservations = [] } = useGetLiveAssetReservationsQuery({ saveId, status: 'open' });
  const [reserve, { isLoading: reserving }] = useReserveLiveAssetMutation();
  const [release, { isLoading: releasing }] = useReleaseLiveAssetReservationMutation();

  const [target, setTarget] = useState<LiveAsset | null>(null);
  const [form] = Form.useForm();

  const submit = async () => {
    if (!target) return;
    try {
      const v = await form.validateFields();
      await reserve({ saveId, plantingId: target.plantingId, payload: { quantity: v.quantity, reason: v.reason, counterpartyRef: v.counterpartyRef } }).unwrap();
      message.success('Зарезервировано');
      setTarget(null);
      form.resetFields();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.data?.message || 'Ошибка резервирования');
    }
  };

  const doRelease = async (reservationId: string) => {
    try {
      await release({ saveId, reservationId }).unwrap();
      message.success('Резерв снят');
    } catch (e: any) {
      message.error(e?.data?.message || 'Ошибка снятия резерва');
    }
  };

  const resvByPlanting = new Map<string, typeof reservations>();
  for (const r of reservations) {
    const list = resvByPlanting.get(r.planting_id) || [];
    list.push(r);
    resvByPlanting.set(r.planting_id, list);
  }

  return (
    <Card title="Живые активы в хранилище (резервации)" loading={isLoading}>
      {assets.length === 0 ? (
        <Empty description="Нет живых посадок в хранилище (holding)." />
      ) : (
        <Table
          rowKey="plantingId"
          size="small"
          dataSource={assets}
          pagination={false}
          columns={[
            {
              title: 'Культура', key: 'crop',
              render: (_: unknown, a: LiveAsset) => (
                <Space direction="vertical" size={0}>
                  <Text strong>{a.cropName || 'Посадка'}</Text>
                  {a.cropLatinName && <Text type="secondary" style={{ fontSize: 12 }}>{a.cropLatinName}</Text>}
                </Space>
              ),
            },
            { title: 'Всего', dataIndex: 'quantity', key: 'q', align: 'right' as const },
            { title: 'Резерв', dataIndex: 'reserved', key: 'r', align: 'right' as const },
            { title: 'Доступно', key: 'a', align: 'right' as const, render: (_: unknown, a: LiveAsset) => <Text strong>{a.available}</Text> },
            {
              title: 'Действия', key: 'act',
              render: (_: unknown, a: LiveAsset) => {
                const resv = resvByPlanting.get(a.plantingId) || [];
                return (
                  <Space wrap size={4}>
                    <Button size="small" type="primary" disabled={a.available <= 0} onClick={() => { form.resetFields(); setTarget(a); }}>
                      Резерв
                    </Button>
                    {resv.map((r) => (
                      <Tag key={r.id} closable closeIcon="снять" onClose={(e) => { e.preventDefault(); doRelease(r.id); }} color="gold">
                        {Number(r.quantity)} шт
                      </Tag>
                    ))}
                  </Space>
                );
              },
            },
          ]}
        />
      )}

      <Modal
        open={!!target}
        title={`Резерв · ${target?.cropName || ''}`}
        onOk={submit}
        onCancel={() => setTarget(null)}
        confirmLoading={reserving || releasing}
        okText="Зарезервировать"
        cancelText="Отмена"
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="quantity" label={`Количество (доступно ${target?.available ?? 0})`} rules={[{ required: true, message: 'Укажите количество' }, { type: 'number', min: 1, message: 'Минимум 1' }]}>
            <InputNumber min={1} max={target?.available} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="reason" label="Причина (опц.)"><Input placeholder="контракт / бронь" /></Form.Item>
          <Form.Item name="counterpartyRef" label="Контрагент (опц.)"><Input /></Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default LiveAssetsSection;
